<template>
  <div class="video-card">
    <video
      ref="videoEl"
      class="video-el"
      :class="{ 'video-loading': videoLoading }"
      :src="video.asset_url"
      preload="metadata"
      playsinline
      @click="togglePlay"
      @ended="playing = false"
      @pause="playing = false"
      @play="playing = true"
      @playing="videoLoading = false"
      @waiting="videoLoading = true"
    />
    <div v-if="videoLoading" class="loading-spinner">
      <i class="pi pi-spin pi-spinner" />
    </div>
    <div v-if="!playing" class="shade" />

    <div class="badges">
      <div class="dur-badge">{{ video.metadata.duration }}s</div>
      <div class="dur-badge">
        {{
          new Date(video.created_at).toLocaleDateString("de-DE", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
        }}
      </div>
    </div>

    <div class="actions">
      <button class="action-btn" title="Download" @click="download">
        <i class="pi pi-download" />
      </button>
      <button
        class="action-btn"
        title="Copy link"
        @click="$emit('copy-link', video.asset_url)"
      >
        <i class="pi pi-link" />
      </button>
      <button
        class="action-btn"
        title="Details"
        @click="$emit('show-info', video)"
      >
        <i class="pi pi-info-circle" />
      </button>
      <button
        class="action-btn"
        title="Transcript"
        :disabled="transcriptLoading"
        @click="$emit('fetch-transcript', video)"
      >
        <i v-if="transcriptLoading" class="pi pi-spin pi-spinner" />
        <template v-else>T</template>
      </button>
    </div>

    <button
      v-if="!playing"
      class="center-play"
      title="Play"
      @click="togglePlay"
    >
      <i class="pi pi-play" />
    </button>

    <div v-if="!playing" class="meta">
      <p class="meta-show">
        <span class="meta-label">Show:</span> {{ video.show_title }}
      </p>
      <p class="meta-episode">
        <span class="meta-label">Episode:</span>
        {{ video.episode_title || "—" }}
      </p>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from "vue";

export default {
  name: "VideoCard",
  props: {
    video: { type: Object, required: true },
    activePlayingId: { type: String, default: null },
    transcriptLoadingId: { type: String, default: null },
  },
  emits: ["play", "copy-link", "show-info", "fetch-transcript"],
  setup(props, { emit }) {
    const videoEl = ref(null);
    const playing = ref(false);
    const videoLoading = ref(false);
    const transcriptLoading = computed(
      () => props.transcriptLoadingId === props.video.asset_id
    );

    const togglePlay = () => {
      if (playing.value) {
        videoEl.value.pause();
        videoLoading.value = false;
      } else {
        emit("play", props.video.asset_id);
        videoLoading.value = true;
        videoEl.value.play().catch(() => {
          videoLoading.value = false;
        });
      }
    };

    watch(
      () => props.activePlayingId,
      (activeId) => {
        if (activeId !== props.video.asset_id && playing.value) {
          videoEl.value.pause();
          videoLoading.value = false;
        }
      }
    );

    const download = async () => {
      try {
        const response = await fetch(props.video.asset_url);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${
          props.video.episode_title || props.video.asset_id
        }.mp4`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error downloading video:", error);
        window.open(props.video.asset_url, "_blank");
      }
    };

    return {
      videoEl,
      playing,
      videoLoading,
      transcriptLoading,
      togglePlay,
      download,
    };
  },
};
</script>

<style scoped>
.video-card {
  position: relative;
  aspect-ratio: 9 / 16;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  border: 1px solid #e7e8ec;
  background: #2a2c31;
}

.video-el {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.video-el.video-loading {
  filter: blur(6px);
}

.loading-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  pointer-events: none;
}

.loading-spinner i {
  color: #fff;
  font-size: 26px;
}

.shade {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 40%,
    rgba(0, 0, 0, 0.55) 72%,
    rgba(0, 0, 0, 0.88) 100%
  );
}

.badges {
  position: absolute;
  top: 8px;
  left: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 2;
  pointer-events: none;
}

.dur-badge {
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 11.5px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 5px;
}

.center-play {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.32);
  border: 1.5px solid rgba(255, 255, 255, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  backdrop-filter: blur(1px);
  cursor: pointer;
}

.center-play i {
  color: #fff;
  font-size: 18px;
  margin-left: 3px;
}

.actions {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  flex-direction: column;
  gap: 7px;
  z-index: 2;
}

.action-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
}

.action-btn i {
  font-size: 13px;
}

.meta {
  position: absolute;
  left: 10px;
  right: 10px;
  bottom: 10px;
  color: #fff;
  z-index: 2;
  pointer-events: none;
}

.meta-label {
  color: #ff5a57;
  font-weight: 800;
}

.meta-show {
  font-size: 12.5px;
  font-weight: 800;
  line-height: 1.2;
  margin: 0 0 3px 0;
  color: #fff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.meta-episode {
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: rgba(255, 255, 255, 0.75);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
